import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  AccordionBody,
  AccordionItem,
  UncontrolledTooltip,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Accordion,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import dayjs from "dayjs"
import { queryGatewayEventLogs } from "helpers/backend_helper"
import {
  dateFormatter,
  dateSorter,
  defaultPageSizeOptions,
  showToast,
  stringSorter,
  downloadExcel,
} from "utils/general"
import CustomRangePicker from "components/DatePickers/RangePicker"
import { Table as AntdTable, Input as AntdInput, InputNumber, Select, } from "antd"
import { tableDefaultHeight } from "constants/layout"
const { Search } = AntdInput

const GatewayLogs = props => {
  const [logs, setLogs] = useState([])
  const [ongoingSearch, setOngoingSearch] = useState(false)
  const [maxItems, setMaxItems] = useState("500")
  const [eventsFilter, setEventsFilter] = useState([])
  const [nameFilter, setNameFilter] = useState("")
  const [filtersOpen, setFiltersOpen] = useState("")
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "days"),
    dayjs(),
  ])
  const [title] = useState(props.t("Gateway logs"))
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search

  const [eventOptions] = useState([
    { label: "Online", value: 1 },
    { label: "Offline", value: 2 },
    { label: "Shutdown", value: 3 },
    { label: "Battery-operated", value: 4 },
    { label: "AC-powered", value: 5 },
    { label: "Initialized", value: 6 },
    { label: "NPX error", value: 7 },
  ])

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    getLogs()
  }, [dateRange, searchTriggers])

  // Get gateway event logs
  const getLogs = () => {
    if (!ongoingSearch) {
      setOngoingSearch(true)
      let rows = !Number.isNaN(Number.parseFloat(maxItems)) ? Number.parseInt(maxItems, 10) : 500

      queryGatewayEventLogs(
        abortRef.current,
        dateRange[0].valueOf(),
        dateRange[1].valueOf(),
        eventsFilter,
        nameFilter,
        rows,
      )
        .then(result => {
          //console.log("Logs: ", result.entries)
          if (result.statusCode == 200 && result.entries) {
            setLogs(result.entries)
          } else {
            setLogs([])
            showToast(props.t("Error"), "error")
          }
        })
        .catch(e => {
          console.log(e)
          setLogs([])
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setOngoingSearch(false)
        })
    }
  }

  const columns = [
    {
      dataIndex: "timestamp",
      title: props.t("Time"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["timestamp"], b["timestamp"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "event",
      title: props.t("Event"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "event", sortOrder)
      },
      render: eventFormatter,
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "name",
      title: props.t("Name"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "name", sortOrder)
      },
      width: "300px",
      ellipsis: true,
    },
    {
      dataIndex: "key",
      title: props.t("GUID"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "key", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
  ]

  function eventFormatter(cell) {
    if (cell == "Online") {
      return (
        <span className="text-success">
          <i className="mdi mdi-check-bold me-1" />
          {cell.toUpperCase()}
        </span>
      )
    } 
    else if (cell == "Initialized") {
      return (
        <span className="text-primary">
          <i className="mdi mdi-check-bold me-1" />
          {cell.toUpperCase()}
        </span>
      )
    } 
    else if (cell == "AC-powered") {
      return (
        <span className="text-primary">
          <i className="mdi mdi-power-plug me-1" />
          {cell.toUpperCase()}
        </span>
      )
    } 
    else if (cell == "Battery-operated") {
      return (
        <span className="text-tertiary">
          <i className="mdi mdi-battery-50 me-1" />
          {cell.toUpperCase()}
        </span>
      )
    } 
    else if (cell == "Offline") {
      return (
        <span className="text-muted">
          <i className="mdi mdi-network-strength-off me-1" />
          {cell.toUpperCase()}
        </span>
      )
    }
    else if (cell == "Shutdown") {
      return (
        <span className="text-danger">
          <i className="mdi mdi-power-sleep me-1" />
          {cell.toUpperCase()}
        </span>
      )
    }
    else if (cell == "NPX error") {
      return (
        <span className="text-danger">
          <i className="mdi mdi-bug me-1" />
          {cell.toUpperCase()}
        </span>
      )
    }
    else {
      return cell.toUpperCase()
    }
  }

  const onDateSelection = value => {
    setDateRange(value)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [props.t("Time"), props.t("Event"), props.t("Name"), props.t("GUID")],
    ]
    const data = logs.map(elt => [
      dateFormatter(elt.timestamp),
      elt.event,
      elt.name,
      elt.key,
    ])

    downloadExcel(title, heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={props.t("Gateway logs")} />

          <Card>
            <CardBody>
              <Accordion
                flush
                open={filtersOpen}
                toggle={id => {
                  id === filtersOpen ? setFiltersOpen("") : setFiltersOpen(id)
                }}
              >
                <AccordionItem>
                  <AccordionBody
                    accordionId="1"
                    className="mb-3"
                    style={{
                      backgroundColor: "#e8f0fe",
                      border: "1px solid #ced4da",
                    }}
                  >
                    <Row>
                      <Col></Col>
                    </Row>
                  </AccordionBody>
                </AccordionItem>
              </Accordion>
              <Row>
                <Col>
                  {logs != null && (
                    <React.Fragment>
                      <Row>
                        <Col className="mb-3">
                          <Select
                            mode={"multiple"}
                            disabled={ongoingSearch}
                            allowClear={true}
                            value={eventsFilter}
                            placeholder={props.t("Event")}
                            onChange={(v) => { setEventsFilter(v) }}
                            options={eventOptions}
                            style={{ width: 100 + "%", minWidth: "240px" }}
                          />
                        </Col>
                        <Col className="mb-3">
                          <Search
                            placeholder={props.t("Name") + " / GUID"}
                            disabled={ongoingSearch}
                            allowClear
                            value={nameFilter}
                            onChange={v => {
                              setNameFilter(v.target.value)
                            }}
                            onSearch={() => {
                              setSearchTriggers(prevValue => prevValue + 1)
                            }}
                            style={{ minWidth: "200px", width: "100%" }}
                          />
                        </Col>
                        <Col className="col-auto mb-3">
                          <InputNumber
                            style={{ width: "135px" }}
                            suffix={props.t("max")}
                            disabled={ongoingSearch}
                            value={maxItems}
                            step={100}
                            onChange={newVal => {
                              setMaxItems(newVal)
                            }}
                            onPressEnter={() => {
                              getLogs()
                            }}
                          />
                        </Col>
                        <Col style={{ maxWidth: "450px" }}>
                          <div className="mb-3" style={{ minWidth: "350px" }}>
                            <CustomRangePicker
                              value={dateRange}
                              rangeChanged={onDateSelection}
                              showTime={true}
                              allowClear={false}
                            />
                          </div>
                        </Col>
                        <Col className="col-auto mb-3">
                          <Button
                            className="compact-button"
                            color="primary"
                            disabled={ongoingSearch}
                            onClick={() => {
                              getLogs()
                            }}
                          >
                            <i className="mdi mdi-calendar-refresh me-2" />
                            {props.t("Search")}
                          </Button>
                          <Button
                            className="compact-button ms-3"
                            id="calendar-button"
                            color="primary"
                            disabled={ongoingSearch}
                            onClick={() => {
                              setDateRange([dateRange[0], dayjs()])
                            }}
                          >
                            <UncontrolledTooltip
                              placement="bottom"
                              target="calendar-button"
                            >
                              {props.t("Refresh time")}
                            </UncontrolledTooltip>
                            <i className="mdi mdi-calendar-refresh" />
                          </Button>
                        </Col>
                        <Col>
                          <span className="float-end ms-2">
                            <UncontrolledTooltip
                              placement="bottom"
                              target="downloadstatstable"
                            >
                              {props.t("Download")}
                            </UncontrolledTooltip>
                            <Dropdown
                              id="printButtonServicesStats"
                              isOpen={menu}
                              toggle={() => setMenu(!menu)}
                              className="download-btn text-center"
                              type="button"
                            >
                              <DropdownToggle
                                id="downloadstatstable"
                                className="text-muted"
                                tag="i"
                              >
                                <i className="mdi mdi-download" />
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => generateExcel()}>
                                  {props.t("Download .xlsx")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </span>
                        </Col>
                      </Row>

                      <Row className="mt-1">
                        <Col xl="12">
                          <AntdTable
                            className="colored-header narrow-header"
                            bordered
                            size="small"
                            rowKey={"id"}
                            columns={columns}
                            dataSource={logs}
                            pagination={{
                              showTotal: (total) => `${total} ${props.t("found results")}`,
                              showSizeChanger: true,
                              defaultPageSize: 100,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            loading={ongoingSearch}
                            tableLayout="auto"
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

GatewayLogs.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(GatewayLogs)
